import { CSSProperties, useEffect, useState } from 'react'

import i18n from 'modules/core/lib/i18n'
import { MarketPosition } from 'modules/job_catalog/benchmark/types'

interface Props {
  position: MarketPosition
  isActive?: boolean
  onClick?: () => void
  disabled?: boolean
  size?: 'sm' | 'md'
  loading?: boolean
}

const activeBarColorByPosition: Record<MarketPosition, string> = {
  below: 'hsl(var(--warning-50))',
  above: 'hsl(var(--info-50))',
  at_market: 'hsl(var(--positive-50))',
  unknown: 'hsl(var(--neutral-solid-50))',
}

const activeBarIndexByPosition: Record<MarketPosition, number | null> = {
  below: 0,
  at_market: 1,
  above: 2,
  unknown: null,
}

const getSizeStyles = (size: 'sm' | 'md') => {
  if (size === 'sm') {
    return {
      barHeight: 14,
      barWidth: 3,
      barGap: 2,
      wrapperSize: 24,
      wrapperPadding: '0px',
      borderRadius: 8,
    }
  }

  return {
    barHeight: 20,
    barWidth: 3,
    barGap: 3,
    wrapperSize: undefined,
    wrapperPadding: '4px 6px',
    borderRadius: 10,
  }
}

const SalaryIndicator = ({
  position,
  isActive = false,
  onClick,
  disabled = false,
  size = 'sm',
  loading = false,
}: Props) => {
  const [isHovered, setIsHovered] = useState(false)
  const [loadingBarIndex, setLoadingBarIndex] = useState(0)
  const { barHeight, barWidth, barGap, wrapperSize, wrapperPadding, borderRadius } =
    getSizeStyles(size)
  const highlight = activeBarColorByPosition[position]
  const activeBarIndex = activeBarIndexByPosition[position]

  useEffect(() => {
    if (!loading) {
      setLoadingBarIndex(0)
      return
    }

    const delayByBarIndex = [180, 180, 260]
    const timeoutId = window.setTimeout(() => {
      setLoadingBarIndex((currentIndex) => (currentIndex + 1) % 3)
    }, delayByBarIndex[loadingBarIndex])

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [loading, loadingBarIndex])

  const wrapperStyle: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...(wrapperSize ? { width: wrapperSize, height: wrapperSize } : {}),
    padding: wrapperPadding,
    borderRadius,
    border: 'none',
    background:
      onClick && isActive
        ? 'hsl(var(--info-50) / 0.15)'
        : onClick && isHovered && !disabled
          ? 'hsl(var(--neutral-10))'
          : 'transparent',
    cursor: onClick && !disabled ? 'pointer' : 'default',
    transition: 'background-color 120ms ease',
    opacity: disabled ? 0.5 : 1,
  }

  const indicatorBars = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: barGap }}>
      {[0, 1, 2].map((index) => (
        <span
          key={index}
          style={{
            width: barWidth,
            height: barHeight,
            borderRadius: 999,
            background: loading
              ? index === loadingBarIndex
                ? 'hsl(var(--neutral-50))'
                : 'hsl(var(--neutral-20))'
              : index === activeBarIndex
                ? highlight
                : 'hsl(var(--neutral-20))',
          }}
        />
      ))}
    </span>
  )

  if (!onClick) {
    return <span style={wrapperStyle}>{indicatorBars}</span>
  }

  return (
    <button
      type="button"
      aria-label={i18n.t('job_catalog.benchmark.open_details')}
      aria-pressed={isActive}
      disabled={disabled}
      onClick={onClick}
      style={wrapperStyle}
      onMouseEnter={() => {
        if (disabled || isActive) return
        setIsHovered(true)
      }}
      onMouseLeave={() => {
        setIsHovered(false)
      }}
    >
      {indicatorBars}
    </button>
  )
}

export default SalaryIndicator
